import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { useUserRequests } from "@/features/user-requests/hooks/use-user-requests";
import { getBasePath } from "@/helpers/get-base-path";
import { CrmArea } from "@/features/internal-user/services/access";
import { usePermissionContext } from "./permission-context";
import { PermissionType, Role, roleDisplayMap } from "@/helpers/role-permission";
import { hasAccess } from "@/components/common/has-access";
import {
  useHandbookAccess,
  useHandbookSummary,
} from "@/features/handbook/hooks/use-handbook";
import { SignOutButton } from "@clerk/clerk-react";
import {
  Bell,
  BookOpenText,
  BookType,
  ChevronRight,
  ClipboardList,
  Globe,
  LogOut,
  Mail,
  Package,
  FileText,
  Percent,
  Pill,
  Receipt,
  Scale,
  ShieldCheck,
  Stethoscope,
  UserCog,
  Users,
  Video,
} from "lucide-react";
import { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button } from "../ui/button";
import Logo from "./logo";
import RegionToggle from "@/features/regions/ui/region-toggle";
import {
  ALL_REGIONS_CODE,
  regionPath,
  regionlessPath,
} from "@/features/regions/constants";

type NavChild = {
  title: string;
  url: string;
  hasBadge?: boolean;
  /**
   * The verb a role must hold to be offered this child, checked against the
   * parent's area with the same rule the page and the server apply. Declared
   * per child, never per role: a new role gets the link by being given the
   * verb, and nothing here names a role or reads is_super.
   */
  permission?: PermissionType;
  /**
   * Set when this link is an area of its own rather than part of the group's.
   * Users, Forms, Notifications, Supply Config and Audit Center are split per
   * page, so each of their links shows only when this role can view THAT page.
   * Sessions and Bundles keep one area for the group (their second page is a
   * view over the first page's data), so their children leave this unset.
   */
  area?: CrmArea;
};

type NavItem = {
  title: string;
  icon: React.ElementType;
  /**
   * The CRM area this page belongs to. Every standalone nav entry has one, and
   * so do the two groups whose children share it (Sessions, Bundles). A group
   * whose children are areas of their own omits it and is shown when any of
   * its children is viewable.
   */
  area?: CrmArea;
  url?: string;
  children?: NavChild[];
};

const navItems: NavItem[] = [
  {
    title: "Users",
    icon: Users,
    children: [
      { title: "All Users", url: "/users", area: "users" },
      {
        title: "Pending Requests",
        url: "/pending-requests",
        hasBadge: true,
        area: "users_pending_requests",
      },
      {
        title: "Decorator Links",
        url: "/users/decorator-links",
        area: "users_decorator_links",
      },
      { title: "Export", url: "/users/export", area: "users_export" },
    ],
  },
  {
    title: "Sessions",
    icon: Video,
    area: "sessions",
    children: [
      { title: "All Sessions", url: "/sessions" },
      { title: "Couples Pack", url: "/couples-packs" },
    ],
  },
  {
    title: "Prescriptions",
    url: "/prescriptions",
    icon: Pill,
    area: "prescriptions",
  },
  {
    title: "Bundles",
    icon: Package,
    area: "bundles",
    children: [
      { title: "All Bundles", url: "/bundles" },
      { title: "Pricing Catalog", url: "/choose-bundle-link" },
    ],
  },
  {
    title: "Payment Pages",
    icon: FileText,
    url: "/payment-pages",
    area: "payment_pages",
  },
  {
    title: "Supply Config",
    icon: Stethoscope,
    children: [
      {
        title: "Professionals",
        url: "/professionals",
        area: "supply_config_professionals",
      },
      { title: "Teams", url: "/teams", area: "supply_config_teams" },
      {
        title: "Managed Events",
        url: "/managed-events",
        area: "supply_config_managed_events",
      },
      {
        title: "Event Types",
        url: "/event-types",
        area: "supply_config_event_types",
      },
    ],
  },
  { title: "Coupons", icon: Percent, url: "/coupons", area: "coupons" },
  {
    title: "Forms",
    icon: BookType,
    children: [
      { title: " All forms", url: "/forms", area: "forms" },
      { title: "Submissions", url: "/submissions", area: "forms_submissions" },
    ],
  },
  {
    title: "Notifications",
    icon: Bell,
    children: [
      { title: "Whatsapp", url: "/notifications", area: "notifications" },
      { title: "Emails", url: "/emails", area: "notifications_emails" },
      {
        title: "Push Notifications",
        url: "/notifications/broadcast",
        area: "notifications_push",
      },
    ],
  },
  { title: "Invoices", icon: Receipt, url: "/invoices", area: "invoices" },
  {
    title: "OTP Logs",
    icon: ShieldCheck,
    url: "/otp-logs",
    area: "otp_logs",
  },
  {
    title: "Admin Panel",
    icon: UserCog,
    area: "internal_users",
    children: [
      { title: "Internal Users", url: "/admins" },
      {
        title: "Manage Access",
        url: "/admins/manage",
        permission: "manage_admins",
      },
    ],
  },
  {
    title: "Audit Center",
    icon: ClipboardList,
    children: [
      {
        title: "Duplicate Accounts",
        url: "/audit/duplicate-accounts",
        area: "audit_duplicate_accounts",
      },
      {
        title: "Bundle Discrepancy",
        url: "/audit/bundle-discrepancy",
        area: "audit_bundle_discrepancy",
      },
      {
        title: "Unmapped Sessions",
        url: "/audit/unmapped-sessions",
        area: "audit_unmapped_sessions",
      },
    ],
  },
  {
    title: "Legal Documents",
    icon: Scale,
    url: "/legal-documents",
    area: "legal_documents",
  },
];

function PendingRequestsBadge({ isActive }: { isActive: boolean }) {
  // Count all open requests regardless of type (currently PROFESSIONAL_CHANGE
  // + PROFILE_UPDATE) — omitting the type filter returns the combined total.
  const { data } = useUserRequests({
    status: "OPEN",
    page: 0,
    limit: 1,
  });
  const count = data?.total ?? 0;
  if (count === 0) return null;
  return (
    <span
      className={`ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold ${
        isActive ? "bg-red-500 text-white" : "bg-white text-red-500"
      }`}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

function PendingRequestsTopItem({ isActive }: { isActive: boolean }) {
  const { data } = useUserRequests({
    type: "PROFESSIONAL_CHANGE",
    status: "OPEN",
    page: 0,
    limit: 1,
  });
  const count = data?.total ?? 0;

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        className={`${
          isActive
            ? "bg-white text-orange-500 hover:bg-white hover:text-orange-500"
            : "text-white hover:bg-white hover:text-orange-500"
        } rounded-md transition-colors py-5 mb-4 border border-solid border-white`}
      >
        <Link to="/pending-requests">
          <span className="text-base flex-1">Pending Requests</span>
          {count > 0 && (
            <span
              className={`ml-auto flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[12px] font-bold ${
                !isActive ? "text-red-500 bg-white" : "text-white bg-red-500"
              }`}
            >
              {count > 99 ? "99+" : count}
            </span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

/**
 * The Handbook link, pinned directly above Logout (RH-831).
 *
 * It sits outside navItems, so the visibleItems filter never sees it — it has to
 * apply the same rule itself: shown only when this role can View the handbook
 * area, and fail closed while access loads. Readers also don't get an empty tab:
 * it appears once something is published, or for anyone who can write pages.
 */
function HandbookLink({ region, isActive }: { region: string; isActive: boolean }) {
  const { isLoaded, canRead, canEdit } = useHandbookAccess();
  const { data: summary } = useHandbookSummary(isLoaded && canRead && !canEdit);
  if (!isLoaded || !canRead) return null;
  if (!canEdit && !summary?.published_pages) return null;

  return (
    <Button
      asChild
      variant="ghost"
      size="sm"
      className={`w-full justify-start gap-2 transition-colors ${
        isActive
          ? "bg-white text-orange-500 hover:bg-white hover:text-orange-500"
          : "text-white hover:bg-white hover:text-orange-500"
      }`}
    >
      <Link to={regionPath(region, "/handbook")}>
        <BookOpenText className="h-4 w-4" />
        <span>Handbook</span>
      </Link>
    </Button>
  );
}

export default function AppSideBar({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const { region = ALL_REGIONS_CODE } = useParams();
  // Match active state against the path with the region segment stripped.
  const basePath = getBasePath(regionlessPath(location.pathname));
  const access = usePermissionContext();
  const { areas, isSuper, isLoaded, role } = access;
  const displayName = roleDisplayMap[role as Role];

  // Hide what this role cannot open. Presentation only — RolesGuard enforces the
  // same grid server-side, so a hidden page is a refused request too, not merely
  // an invisible link. Fails closed while access is loading, matching
  // <HasAccess>; useMyAccess caches it, so that is one brief moment per session.
  const canView = (area?: CrmArea) => isSuper || !!(area && areas[area]?.view);
  const visibleItems = isLoaded
    ? navItems
        // A group with its own area shows on that area; a group of sub-areas
        // shows when any child does.
        .filter((item) =>
          item.area
            ? canView(item.area)
            : !!item.children?.some((c) => canView(c.area)),
        )
        // Within a visible group, keep only the children this role can open —
        // its own area if it has one — and whose verb the role holds. The same
        // rule the page and the server apply.
        .map((item) =>
          item.children
            ? {
                ...item,
                children: item.children.filter(
                  (c) =>
                    (!c.area || canView(c.area)) &&
                    (!c.permission ||
                      hasAccess(access, c.permission, c.area ?? item.area)),
                ),
              }
            : item,
        )
    : [];

  const activeClass = "bg-white text-orange-500 hover:bg-white hover:text-orange-500";
  const inactiveClass = "text-white hover:bg-white hover:text-orange-500";

  // Pick the single most-specific child URL that matches the current path.
  // A child matches if its url equals the path or is a path prefix; longest
  // matching url wins so /users/export beats /users on /users/export.
  const activeChildUrl = (() => {
    const path = regionlessPath(location.pathname);
    const candidates: string[] = [];
    navItems.forEach((item) => {
      if (!item.children) return;
      item.children.forEach((c) => {
        if (path === c.url || path.startsWith(c.url + "/")) {
          candidates.push(c.url);
        }
      });
    });
    return candidates.sort((a, b) => b.length - a.length)[0] ?? null;
  })();

  // Default groups open if they contain the active route
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    navItems.forEach((item) => {
      if (item.children) {
        initial[item.title] = item.children.some(
          (c) => c.url === activeChildUrl,
        );
      }
    });
    return initial;
  });

  const toggleGroup = (title: string) => {
    setOpenGroups((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  return (
    <SidebarProvider>
      <Sidebar className="bg-orange-500">
        <SidebarHeader className="px-4 pt-6 pb-2">
          <div className="flex items-center justify-between gap-1">
            <Logo />
            <RegionToggle />
          </div>
          <p className="text-sm text-white">{displayName}</p>
        </SidebarHeader>
        <SidebarContent className="flex flex-col justify-between h-full px-1 py-4">
          <div className="flex-1">
            <SidebarGroup>
              <SidebarGroupContent>
                <SidebarMenu className="space-y-1">
                  {visibleItems.map((item) => {
                    if (item.children) {
                      const isGroupActive = item.children.some(
                        (c) => c.url === activeChildUrl,
                      );
                      const isOpen = !!openGroups[item.title];

                      return (
                        <SidebarMenuItem key={item.title}>
                          {/* Collapsible parent label */}
                          <button
                            onClick={() => toggleGroup(item.title)}
                            className={`flex w-full items-center gap-2 px-2 py-1.5 rounded-md transition-colors ${
                              isGroupActive
                                ? "text-white"
                                : "text-white/80 hover:text-white"
                            }`}
                          >
                            <item.icon className="h-4 w-4 shrink-0" />
                            <span className="text-base flex-1 text-left">
                              {item.title}
                            </span>
                            {/* Show badge on parent only when collapsed */}
                            {!isOpen && item.children.some((c) => c.hasBadge) && (
                              <PendingRequestsBadge isActive={isGroupActive} />
                            )}
                            <ChevronRight
                              className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
                                isOpen ? "rotate-90" : ""
                              }`}
                            />
                          </button>

                          {/* Sub-items — show/hide with smooth height transition */}
                          <div
                            className={`grid transition-all duration-200 ${
                              isOpen
                                ? "grid-rows-[1fr] opacity-100"
                                : "grid-rows-[0fr] opacity-0"
                            }`}
                          >
                            <div className="overflow-hidden">
                              <SidebarMenuSub className="border-white/30">
                                {item.children.map((child) => {
                                  const isChildActive =
                                    child.url === activeChildUrl;
                                  return (
                                    <SidebarMenuSubItem key={child.url}>
                                      <SidebarMenuSubButton
                                        asChild
                                        isActive={isChildActive}
                                        className={`${
                                          isChildActive
                                            ? activeClass
                                            : inactiveClass
                                        } rounded-md transition-colors h-8`}
                                      >
                                        <Link
                                          to={regionPath(region, child.url)}
                                          className="flex items-center w-full"
                                        >
                                          <span className="text-sm flex-1">
                                            {child.title}
                                          </span>
                                          {child.hasBadge && (
                                            <PendingRequestsBadge
                                              isActive={isChildActive}
                                            />
                                          )}
                                        </Link>
                                      </SidebarMenuSubButton>
                                    </SidebarMenuSubItem>
                                  );
                                })}
                              </SidebarMenuSub>
                            </div>
                          </div>
                        </SidebarMenuItem>
                      );
                    }

                    // Standalone item
                    const isActive = item.url === basePath;
                    return (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          className={`${
                            isActive ? activeClass : inactiveClass
                          } rounded-md transition-colors`}
                        >
                          <Link to={regionPath(region, item.url!)}>
                            <item.icon className="text-current" />
                            <span className="text-base">{item.title}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </div>

          <div className="mt-auto space-y-1 pt-4">
            <HandbookLink region={region} isActive={basePath === "/handbook"} />
            <SignOutButton>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-white hover:bg-white hover:text-orange-500 justify-start gap-2 transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span>Logout</span>
              </Button>
            </SignOutButton>
          </div>
        </SidebarContent>
      </Sidebar>
      <main className="w-full 2xl:w-[1500px] my-4">{children}</main>
    </SidebarProvider>
  );
}
